'use client'

import type { RegionConfig, IPRedirectBannerCopy } from './type.js'

/** 横幅样式配置 */
export type RegionBannerStyles = {
  /** 背景色，默认 #fff */
  backgroundColor?: string
  /** 按钮圆角，默认 9999px */
  buttonBorderRadius?: string
  /** 文案字号，默认 14px */
  fontSize?: string
  /** Stay Here 按钮背景色，默认 #000 */
  stayButtonBgColor?: string
  /** Stay Here 按钮文字颜色，默认 #fff */
  stayButtonTextColor?: string
  /** Switch Site 按钮边框颜色，默认 #000 */
  switchButtonBorderColor?: string
  /** Switch Site 按钮文字颜色，默认 #000 */
  switchButtonTextColor?: string
  /** 大屏幕（≥1920px）最大宽度，默认 1664px */
  maxWidth?: string
}

export type RegionBannerProps = {
  /** 控制横幅显示/隐藏 */
  visible: boolean
  /** 横幅状态变化回调 */
  onVisibleChange: (visible: boolean) => void
  /** 文案配置 */
  copy: IPRedirectBannerCopy
  /** 点击 Stay Here 按钮的回调 */
  onStayHere: () => void
  /** 点击 Switch Site 按钮的回调 */
  onSwitchSite: () => void
  /** 样式配置 */
  styles?: RegionBannerStyles
  /** 当前站点地区配置（用于显示国旗图标） */
  currentRegion?: RegionConfig
  /** 自定义类名 */
  className?: string
}

// 默认样式
const defaultStyles: Required<RegionBannerStyles> = {
  backgroundColor: '#fff',
  buttonBorderRadius: '9999px',
  fontSize: '14px',
  stayButtonBgColor: '#000',
  stayButtonTextColor: '#fff',
  switchButtonBorderColor: '#000',
  switchButtonTextColor: '#000',
  maxWidth: '1664px',
}

export function RegionBanner({
  visible,
  onVisibleChange,
  copy,
  onStayHere,
  onSwitchSite,
  styles: customStyles,
  currentRegion,
  className,
}: RegionBannerProps) {
  // 合并样式
  const styles = { ...defaultStyles, ...customStyles }

  if (!visible) return null

  const handleStayHere = () => {
    onStayHere()
    onVisibleChange(false)
  }

  const handleClose = () => {
    // 关闭按钮和 Stay Here 按钮行为一致
    onStayHere()
    onVisibleChange(false)
  }

  return (
    <>
      <style>{`
        .region-banner-wrapper {
          height: auto;
          min-height: 74px;
        }
        @media (min-width: 1024px) {
          .region-banner-wrapper {
            height: 54px;
          }
        }
        .region-banner-content {
          padding: 0 12px;
          flex-direction: column;
          gap: 8px;
          align-items: flex-start;
        }
        .region-banner-title-row {
          display: flex;
          align-items: center;
          width: 100%;
          gap: 8px;
          height: auto;
          margin-top: 8px;
        }
        .region-banner-content h1 {
          flex: 1;
          min-width: 0;
        }
        .region-banner-close-btn {
          position: static;
          flex-shrink: 0;
        }
        @media (min-width: 1024px) {
          .region-banner-content {
            padding: 0 64px;
            flex-direction: row;
            align-items: center;
          }
          .region-banner-title-row {
            flex: 1;
            height: 20px;
          }
          .region-banner-close-btn {
            position: absolute;
            right: 16px;
            top: 50%;
            transform: translateY(-50%);
          }
        }
        @media (min-width: 1920px) {
          .region-banner-wrapper {
            display: flex;
            justify-content: center;
          }
          .region-banner-content {
            max-width: ${styles.maxWidth};
            width: 100%;
            position: relative;
            padding-left: 0px;
          }
        }
        @media (min-width: 1025px) and (max-width: 1440px) {
          .region-banner-title {
            max-width: 623px;
          }
        }
      `}</style>

      <div
        className={`region-banner-wrapper relative left-0 top-0 w-full ${className || ''}`}
        style={{ backgroundColor: styles.backgroundColor, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 1000 }}
      >
        <div className="region-banner-content flex h-full justify-between text-black">
          <div className="region-banner-title-row">
            <h1 className="region-banner-title font-bold" style={{ fontSize: styles.fontSize, color: '#1F2021' }}>
              {copy.message}
            </h1>

            {/* 关闭按钮 */}
            <button
              onClick={handleClose}
              className="region-banner-close-btn inline-flex size-[28px] items-center justify-center rounded-full hover:bg-[#f5f5f5] focus:outline-none"
              style={{ color: '#767880' }}
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="l-tablet:ml-0 l-tablet:mb-[8px] ml-4 flex gap-3">
            {/* Stay Here 按钮 */}
            <button
              onClick={handleStayHere}
              className="box-border flex h-[38px] items-center gap-1 whitespace-nowrap px-4 py-2 hover:opacity-80"
              style={{
                backgroundColor: styles.stayButtonBgColor,
                color: styles.stayButtonTextColor,
                fontWeight: '700',
                fontSize: styles.fontSize,
                borderRadius: styles.buttonBorderRadius,
                border: 'none',
              }}
            >
              {currentRegion?.icon && (
                <img src={currentRegion.icon} alt="" style={{ width: '16px', height: '16px', borderRadius: '100%' }} />
              )}
              {copy.stayButtonText}
            </button>

            {/* Switch Site 按钮 */}
            <button
              onClick={onSwitchSite}
              className="box-border flex h-[38px] items-center gap-1 whitespace-nowrap px-4 py-2 hover:opacity-80"
              style={{
                backgroundColor: 'transparent',
                color: styles.switchButtonTextColor,
                border: `1.6px solid ${styles.switchButtonBorderColor}`,
                fontWeight: '700',
                fontSize: styles.fontSize,
                borderRadius: styles.buttonBorderRadius,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.29883 1.2041C12.216 1.20425 15.3923 4.37181 15.3926 8.28027C15.3926 12.189 12.2162 15.3563 8.29883 15.3564C4.38145 15.3563 1.2041 12.189 1.2041 8.28027C1.20439 4.37183 4.38163 1.20428 8.29883 1.2041ZM3.58105 11.4795C4.31053 12.5471 5.39411 13.3542 6.66406 13.7324C6.21155 13.0301 5.86128 12.2699 5.60938 11.4795H3.58105ZM11.0166 11.4795C10.7696 12.2779 10.4256 13.0347 9.98438 13.7178C11.2325 13.3333 12.2971 12.5331 13.0166 11.4795H11.0166ZM7.0791 11.4795C7.3755 12.2654 7.78717 12.9997 8.31445 13.6455C8.83791 13.0305 9.25428 12.2929 9.55273 11.4795H7.0791ZM2.85645 6.56738C2.6854 7.10801 2.59283 7.68338 2.59277 8.28027C2.59277 8.91383 2.69891 9.52251 2.89063 10.0918H5.27148C5.15636 9.42693 5.10277 8.75388 5.11133 8.08789C5.11789 7.57837 5.1619 7.06931 5.23926 6.56738L2.85645 6.56738ZM11.3447 6.56738C11.4483 7.20036 11.4944 7.84036 11.4863 8.47363C11.4793 9.01629 11.4291 9.55828 11.3418 10.0918H13.707C13.8986 9.52259 14.0039 8.91374 14.0039 8.28027C14.0039 7.68337 13.9112 7.10802 13.7402 6.56738H11.3447ZM6.64648 6.56738C6.55761 7.06802 6.50673 7.58339 6.5 8.10547C6.49143 8.7725 6.55341 9.44067 6.68359 10.0918L9.93457 10.0918C10.0348 9.56074 10.0905 9.01207 10.0977 8.45605C10.1058 7.82231 10.0497 7.18763 9.93262 6.56738L6.64648 6.56738ZM6.6123 2.84277C5.32602 3.23909 4.23535 4.07819 3.51563 5.17969L5.5498 5.17969C5.79857 4.34514 6.15294 3.55384 6.6123 2.84277ZM9.93262 2.8291C10.4039 3.56047 10.7656 4.35407 11.0195 5.17969H13.0811C12.3519 4.06343 11.2413 3.21866 9.93262 2.8291ZM8.28223 2.91504C7.73775 3.55467 7.30932 4.32752 7.00879 5.17969L9.55566 5.17969C9.25672 4.35642 8.83179 3.58782 8.28223 2.91504Z"
                  fill="currentColor"
                />
              </svg>
              {copy.switchButtonText}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
